/* eslint-disable @typescript-eslint/no-deprecated */
import { useNumberField } from '@react-aria/numberfield';
import { useNumberFieldState } from '@react-stately/numberfield';
import clsx from 'clsx';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type KeyboardEventHandler,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { FocusEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import * as styles from '../_base-input/BaseInput.sty.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import { getSafeRegionalFormat } from '../forms-core/time/helpers.js';
import { autofillCSS } from '../hooks/useAutofillEvent.sty.js';
import { useAutofillEvent } from '../hooks/useAutofillEvent.js';
import type { FormControlRef } from '../shared-types.js';

// #region Typings
/**
 * The properties of the NumberInput component.
 * @public
 * @deprecated This type is deprecated and will be removed in a future release. Please use the NumberInputV2Props instead.
 */
export type NumberInputProps = Pick<
  BaseInputProps,
  | 'autoComplete'
  | 'placeholder'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'onKeyUp'
  | 'className'
  | 'style'
  | 'data-testid'
  | 'data-dtrum-mask'
  | 'data-dtrum-allow'
> &
  FormControlProps<number | null, (value: number | null) => void> &
  FocusEvents &
  BehaviorTrackingProps & {
    /** Handler that is called when a key is pressed. */
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    /** Minimum amount of the input. */
    min?: number | string;
    /** Maximum amount of the input. */
    max?: number | string;
    /** Defines the step size in which increments/decrements can be performed on the input. */
    step?: number;
  };
// #endregion

/**
 * NumberInputBase component. This is necessary in order to correctly call onChange with undefined on blur, which is otherwise set to NaN by react-stately.
 * @public
 */
export const NumberInputBase = /* @__PURE__ */ forwardRef<
  Omit<FormControlRef<HTMLDivElement>, 'validate'>,
  NumberInputProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage({
    defaultMessage: 'Add number',
    id: 'rrkUQoVNb46Jbd/8',
    description: 'Default placeholder for the number input.',
  });

  const {
    id: propId,
    defaultValue,
    value,
    name,
    onChange,
    disabled: propDisabled,
    readOnly: propReadOnly,
    required: propRequired,
    min: minValueNumberLike,
    max: maxValueNumberLike,
    step,
    placeholder = defaultPlaceholder,
    controlState,
    onKeyUp,
    onKeyDown,
    onBlur,
    onFocus,
    onFocusChange,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  }: NumberInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const componentName = (restProps as Record<string, unknown>)[
    'data-dt-component'
  ];

  const { 'aria-labelledby': ariaLabelledBy, ...remainingAriaProps } =
    useAriaLabelingProps(restProps);
  const locale = useMemo(() => getSafeRegionalFormat(), []);

  // Cast min and max to number since we accept string as well to better match native number inputs
  const minValue = !isNaN(Number(minValueNumberLike))
    ? Number(minValueNumberLike)
    : undefined;
  const maxValue = !isNaN(Number(maxValueNumberLike))
    ? Number(maxValueNumberLike)
    : undefined;

  const numberInputValue = value ?? NaN; // necessary so we can clear the inputValue; otherwise, it snaps back to the previously valid inputValue

  const {
    disabled,
    required,
    id: contextId,
    ariaDisabled,
    readOnly: isReadOnly,
    inputProps: inputHintAriaProps,
    hintProps,
    hint,
    showError: hasError,
  } = useFormControl({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
    propReadOnly,
    controlState,
    validationVisibility: 'immediate',
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const state = useNumberFieldState({
    value: numberInputValue,
    onChange,
    placeholder,
    minValue,
    maxValue,
    step,
    isDisabled: disabled,
    isReadOnly,
    isRequired: required,
    locale,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(
    forwardedRef,
    (): Omit<FormControlRef<HTMLDivElement>, 'validate'> => ({
      element: wrapperRef.current,
      inputRef: inputRef.current,
      focus() {
        inputRef.current?.focus();
      },
    }),
    [],
  );

  const { inputProps } = useNumberField(
    {
      value: numberInputValue,
      onChange,
      id: contextId,
      placeholder,
      minValue,
      maxValue,
      step,
      isDisabled: disabled,
      isReadOnly,
      isRequired: required,
      onKeyUp,
      onKeyDown,
      onBlur,
      onFocus,
      onFocusChange,
      // we pass the aria-labelledBy only if there's no aria-label, because the useNumberField would add aria-labelledby
      // with 2 ids and append the input text to its aria-label otherwise, breaking the getByLabelText selectors in tests otherwise
      'aria-labelledby': remainingAriaProps['aria-label']
        ? undefined
        : ariaLabelledBy,

      ...remainingAriaProps, // passing the aria props here prevents unnecessary react-aria warnings when aria-labelling props are already set
    },
    state,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
    // @ts-ignore React 19 - proper overlap for the ref is no longer there. It should be fine, but the Numberfield will be reworked anyways
    inputRef,
  );
  let inputDefaultValue: string | undefined;

  if (defaultValue !== null && defaultValue !== undefined) {
    inputDefaultValue = defaultValue.toString();
  } else {
    inputDefaultValue =
      value === undefined && !onChange
        ? '' // this prevents warnings about switching from uncontrolled to controlled inputs when no value or defaultValue is set, but onChange used without a value/defaultValue will still throw it
        : undefined;
  }

  const {
    value: inputPropsValue,
    type,
    id,
    readOnly = false,
    role,
    onKeyDown: onKeyDownReactAria,
    ...remainingInputProps
  } = inputProps;

  const [inputValue, setInputValue] = useControllableState({
    defaultValue: inputDefaultValue,
    value: inputPropsValue?.toString(),
  });

  const { isAutoFilled, onAnimationStart } = useAutofillEvent();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    remainingInputProps?.onChange?.(event);
    setInputValue(event.target.value);
  };

  const { focusProps, focusClassName } = useFocusRing({
    color: hasError && !(disabled || ariaDisabled) ? 'critical' : 'neutral',
    ignoreModality: true,
    disabled,
  });

  //TODO: replace this with the BaseInput once the rework is done and we no longer depend on react-aria. This introduced the limitation of passing the inputRef from the BaseInput to the NumberInputBase, as it's initially null and useNumberField instantly breaks instead of waiting for failing gracefully and then resetting the ref on the next render
  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={wrapperRef}
      className={consumerClassName}
      style={consumerStyle}
      {...behavioralTrackingProps}
      data-dt-component={componentName}
    >
      <Flex
        gap={2}
        alignItems="center"
        className={clsx(styles.wrapper, styles.variant['default'], {
          [styles.hasError]: hasError,
          [styles.disabled]: disabledOrAriaDisabled,
          [styles.readOnly]: readOnly,
        })}
      >
        <div
          className={clsx(
            styles.container,
            inputGroupClassName,
            focusClassName,
            {
              [styles.isAutoFilled]: isAutoFilled,
            },
          )}
        >
          <input
            id={id}
            ref={inputRef}
            type="text"
            role={role}
            onChange={handleInputChange}
            onAnimationStart={onAnimationStart}
            disabled={disabled}
            className={clsx(styles.input, autofillCSS)}
            readOnly={readOnly}
            {...(_mergeProps(
              remainingInputProps,
              restProps,
              focusProps,
              inputHintAriaProps,
            ) as unknown as Record<string, unknown>)}
            name={name}
            required={required} // react-aria only sets aria-required in the remainingInputProps, so we need to override it with the context prop here
            value={inputValue}
            aria-disabled={ariaDisabled}
            // react-aria prevents default events, so it behaves differently from our TextInput, which has some undesired issues - see ADES-3971 and https://github.com/adobe/react-spectrum/issues/2803
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (
                (e.key === 'Home' && minValue === undefined) ||
                (e.key === 'End' && maxValue === undefined)
              ) {
                onKeyDown?.(e);
                return; // opt out of using react-aria's keydown logic in this case, because it would prevent default behavior (moving the cursor to the front/end with Home/End)
              }

              onKeyDownReactAria?.(e); // we still need to use react aria's events when we do have a min/max (then the value will be snapped to these when pressing Home/End) or for incrementing/decrementing the value on pressing Up/Down keys, but these will no longer be necessary as soon as we implement the spin button
            }}
          />
        </div>
      </Flex>
      {hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
});

(
  NumberInputBase as typeof NumberInputBase & { displayName: string }
).displayName = 'NumberInputBase';
